﻿import React, { useEffect, useState } from 'react'
import { createStyles, withStyles } from '@mui/styles'
import {
    Paper, Accordion, AccordionSummary, AccordionDetails, FormControlLabel, Checkbox, IconButton, Tooltip, Dialog,
    DialogTitle, DialogContent, DialogActions, Typography
} from '@mui/material'
import { DirectionsBoatOutlined, ExpandMore, GavelOutlined, History, Add } from '@mui/icons-material'
import { t } from '../../../infrastructure/i18nextHelper'
import { Popup, AttachFileButton, Button, ButtonPopupParams, CustomDialog, Stepper, NumberField } from '../../common/customComponents'
import { muiOptions, defaultStyles, defaultColors } from '../../../infrastructure/materialUiThemeProvider'
import { CreateMovementFromDeal, DuplicateDealCommand, DealStatus, ForecastDailyPurchaseStatus, DealMovementType } from '../dealModels'
import { TitleSection, ReferenceNumberSection, SiteSection, VolumeSection, TolerancesSection, CommentSection } from './_dealGeneralInformations'
import { PricingFirstLine, PricingSecondLine, PricingThirdLine } from './_dealPricingInformations'
import { VesselEditDialogContainer } from '../../vessels/vesselEdit/vesselEditStore'
import { Claims } from '../../../infrastructure/signIn/models'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { DealContainer, DealDialogContainer, dealDialog } from './dealEditStore'
import { DealSapForm } from './_dealSapForm'
import { useActionDebounce } from '../../common/debounce'
import { ValidationProcess } from './_validationProcess'
import { hasFeature } from '../../../infrastructure/feature'
import { pricingDialog, PricingDialogContainer } from '../../../app/pricings/pricingDialog'
import { popupNavigator } from '../../../infrastructure/popupNavigator'
import { CustomButton } from '../../common/components/button'
import guid from '../../../infrastructure/guid'
import DealMovements from './dealMovements'
import { DealStatusChip } from './statusChip'
import * as Component from './_dealComponents'
import { DuplicateDealDialog, DealDuplicatePeriod } from '../../common/duplicate/duplicateDealDialog'
import { historyDialog } from '../../common/history/dialog'
import { snackbars } from '../../../infrastructure/snackbars'
import { vesselNavigationDialog } from './vesselNavigationDialog'
import { MovementType } from '../../stock/stockModels'

function DealEdit({ classes }) {
    let store = DealContainer.useContainer()
    let dealDialogStore = DealDialogContainer.useContainer()
    let pricingDialogStore = PricingDialogContainer.useContainer()
    let vessel = VesselEditDialogContainer.useContainer()
    let handleSaveDebouncer = useActionDebounce(save)
    let handleDuplicateDebouncer = useActionDebounce(duplicateDeal)
    let handleAssignMovementsDebouncer = useActionDebounce(assingMovements)
    let handleUnAssignMovementsDebouncer = useActionDebounce(unAssingMovement)
    let handleCreateMovementDebouncer = useActionDebounce(createMovement)
    let [duplicateConfirmDialogOpen, setDuplicateConfirmDialogOpen] = useState<boolean>(false)
    let [showPopupCreateMovement, setShowPopupCreateMovement] = useState<boolean>(false)
    let [newMovementType, setNewMovementType] = useState<string | null>(null)

    async function save(refreshAfterSave = true): Promise<boolean> { return store.saveIfNoFieldErrors(refreshAfterSave) }

    async function duplicateDeal() {
        let command: DuplicateDealCommand = {
            sourceId: store.dealId!,
            duplicateId: guid.createNew(),
            start: store.isPeriodMode ? store.duplicatePeriod?.start! : store.deal?.validFrom!,
            end: store.isPeriodMode ? store.duplicatePeriod?.end! : store.deal?.validTo!
        }
        handleClose()
        await store.duplicateDeal(command)
        popupNavigator.open('deal', command.duplicateId)
    }

    async function createMovement() {
        let command: CreateMovementFromDeal = {
            dealId: store.dealId!,
            movementId: guid.createNew(),
            movementType: newMovementType
        }
        if (await save(false)) {
            let waitCorrelationId = await store.createMovement(command)
            setNewMovementType(null)
            popupNavigator.open('movement', command.movementId, { waitCorrelationId })
        }
    }

    async function assingMovements() {
        let selectedMovements = store.selectedMovements
        if (selectedMovements?.length === 0) return
        let movementIds = selectedMovements?.map(x => x.id)
        await store.assignMovements(movementIds)
    }

    async function unAssingMovement(movementId: string) {
        if (!movementId) return
        await store.unAssignMovement(movementId)
    }

    let handleClose = () => {
        dealDialog.close()
    }

    let openPricing = async () => {
        if (!store.dealId) return
        if (hasClaim(Claims.DealManager)) {
            if (await save())
                pricingDialog.open({ id: store.dealId, pricingType: store.deal?.pricingType ?? null, costGroup: undefined })
        }
        else
            pricingDialog.open({ id: store.dealId, pricingType: store.deal?.pricingType ?? null, costGroup: undefined })
    }


    let openVessel = () => {
        if (store.associatedVessels.length === 0) return
        let options = store.deal?.productId
            ? { productId: store.deal?.productId }
            : undefined
        if (store.associatedVessels.length === 1)
            popupNavigator.open('vessel', store.associatedVessels[0].id, options)
        else
            vesselNavigationDialog.open({ vessels: store.associatedVessels, productId: store.deal?.productId })
    }

    let buttons: ButtonPopupParams[] = [{
        buttonEffect: () => setDuplicateConfirmDialogOpen(true),
        buttonText: t('deals.label.duplicate'),
        hideButton: store.locked(false) || !(store.isAssociatedToAMovement() || store.isAutonomous()) || store.isBorrowLoan()
    },
    {
        buttonEffect: handleSaveDebouncer.execute,
        buttonText: t('components.updateButton'),
        hideButton: store.locked(false)
    }]

    useEffect(() => {
        if (store.associatedVessels.length > 0 && !vessel.isOpen && store.dealId) {
            store.loadDeal(store.dealId)
        }
    }, [vessel.isOpen])

    useEffect(() => {
        if (!pricingDialogStore.isOpen)
            store.loadDeal(store.dealId)
    }, [pricingDialogStore.isOpen])

    useEffect(() => {
        let period: DealDuplicatePeriod = {
            start: store.isPeriodMode ? store.addOneMonth(store.deal?.validFrom!) : store.deal?.validFrom!,
            end: store.isPeriodMode ? store.addOneMonth(store.deal?.validTo!) : store.deal?.validTo!
        }
        store.setDuplicatePeriod(period)
    }, [duplicateConfirmDialogOpen])

    let closeConfirmationPopup = () => {
        store.setUnAssignConfirmDialogOpen(false)
        store.loadDeal(store.dealId)
    }

    let canApplyForecast = () => store.isSaleOnTruckDeal()

    let onClickDealForecastMarketingSales = async () => {
        if (!store.deal?.counterpartyId) {
            snackbars.error(t('deals.label.impossibleMktSaleCreation'))
            return
        }
        if (await save())
            await store.forecastMktSales({ dealId: store.dealId! })
    }

    let handleStatusChange = (index: number) => {
        if (!store.deal || (index !== 3 && store.locked())) return
        let newStatus = ''

        switch (index) {
            case 0: newStatus = DealStatus.Mandate; break;
            case 1: newStatus = DealStatus.Negociated; break;
            case 2: newStatus = DealStatus.Closed; break;
            case 3: newStatus = DealStatus.Cancelled; break;
        }

        if (newStatus === DealStatus.Cancelled && store.hasMovementsOrMktSales()) {
            store.setDealStatusDialog({ isOpen: true, title: t('deals.label.CancelDeal'), description: t('deals.label.impossibleCancelDeal') })
            return
        }

        if ((newStatus === DealStatus.Negociated || newStatus === DealStatus.Closed) && !store.deal.counterpartyId) {
            store.setDealStatusDialog({ isOpen: true, title: t('deals.label.changeDealStatus'), description: t('deals.label.impossibleChangeDealStatus') })
            return
        }

        store.setDeal({ ...store.deal, status: newStatus.toString() })
    }

    let getStepIndexByStatus = (status: string) => {
        switch (status) {
            case DealStatus.Mandate: return 0
            case DealStatus.Negociated: return 1
            case DealStatus.Closed: return 2
            case DealStatus.Cancelled: return 3
            default: return -1
        }
    }

    let isFormulaGenericPricing = store.deal?.pricingType == 'Formula' && hasFeature('GenericPricing')

    let canAddMovement = () =>
        store.canAssignMovement()
        && !store.isInternal()
        && !store.isSaleOnTruckDeal()
        && store.forecastDailyPurchaseStatus === ForecastDailyPurchaseStatus.None
        && !store.isPaperTransaction()
        && store.hasActiveStatus()
        && !store.locked(false)
        && !store.isBorrowLoan()

    let openHistory = () => {
        if (!store?.deal) return
        let product = store.products.find(x => x.id == store.deal!.productId)

        historyDialog.open({
            id: store.deal!.id,
            type: 'deal',
            name: store.deal!.referenceNumber ?? ''
        })
    }

    let newBorrowLoanMovementHandler = (movementType: string) => {
        setNewMovementType(movementType)
        setShowPopupCreateMovement(true)
    }

    function borrowLoanButtonDisabled(button: string): boolean {
        if (store.deal?.status === DealStatus.Closed) return true

        let hasBorrowMovement = store.deal?.movements?.some(x => x.movementType === MovementType.Borrow)
        let hasLoanMovement = store.deal?.movements?.some(x => x.movementType === MovementType.Loan)
        if (button == "BORROW" && ((store.deal?.movementType === "BORROW" && hasLoanMovement)
            || (store.deal?.movementType === "LOAN" && !hasLoanMovement))) {
            return true
        }

        if (button == "LOAN" && ((store.deal?.movementType === "LOAN" && hasBorrowMovement)
            || (store.deal?.movementType === "BORROW" && !hasBorrowMovement))) {
            return true
        }
        return false
    }

    return (
        <>
            <Popup title={t('deals.popup.title')}
                close={handleClose}
                isOpen={dealDialogStore.isOpen}
                buttons={buttons}>
                <div>
                    {store.deal && dealDialogStore.isOpen ?
                        <>
                            {hasFeature('DealStatus') &&
                                <Paper className={classes.dealStatusInformation}>
                                    <div className={classes.stepper}>
                                        <Stepper steps={['', '', '']}
                                            icons={[DealStatus.Mandate, DealStatus.Negociated, DealStatus.Closed]
                                                .map(x => <DealStatusChip key={x} status={x} disabled={store.deal!.status !== x} hoverable />)
                                            }
                                            activeStep={getStepIndexByStatus(store.deal!.status?.toString())}
                                            ignoreStepStyles
                                            classesOverride={{ root: classes.dealStepper }}
                                            onStepChange={(index: number) => handleStatusChange(index)} />
                                    </div>
                                    <div className={classes.cancelledDeal}>
                                        <Stepper steps={['']}
                                            icons={[
                                                <DealStatusChip key={DealStatus.Cancelled} status={DealStatus.Cancelled} disabled={store.deal!.status !== DealStatus.Cancelled} hoverable />]}
                                            activeStep={getStepIndexByStatus(store.deal!.status?.toString())}
                                            ignoreStepStyles
                                            classesOverride={{ root: classes.dealStepper }}
                                            onStepChange={() => handleStatusChange(3)} />
                                    </div>
                                </Paper>
                            }
                            <Paper className={classes.dealInformation}>
                                <div className={classes.buttonDiv}>
                                    <TitleSection />
                                    <div className={classes.headerRight}>
                                        <div className={classes.headerText}>
                                            <Tooltip title={<Typography variant='body1'>{t('history.open')}</Typography>} placement='top'>
                                                <IconButton onClick={openHistory}>
                                                    <History />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                        <div className={classes.headerText}>
                                            <FormControlLabel
                                                disabled={store.locked(false)}
                                                control={<Checkbox onChange={x => store.setDeal({ ...store.deal!, signed: !store.deal!.signed })}
                                                    checked={store.deal!.signed} />}
                                                label={t('deals.label.signed')}
                                            />
                                        </div>
                                        {canApplyForecast() && !store.isInternal()
                                            ? store.deal?.generateMktSaleForecasts
                                                ? <Typography variant='h6' className={classes.forecastCreatedText}>{t('deals.label.marketingSalesForecastCreated')}</Typography>
                                                : !store.locked(false)
                                                    ? <CustomButton
                                                        label={t('deals.label.forecastMarketingSales')}
                                                        className={classes.secondaryButtonForecast}
                                                        waitPromise
                                                        onClick={onClickDealForecastMarketingSales} />
                                                    : null
                                            : null
                                        }
                                        {store.forecastDailyPurchaseStatus === ForecastDailyPurchaseStatus.CanCreates && !store.locked(false) &&
                                            <CustomButton
                                                label={t('deals.label.forecastDailyPurchases')}
                                                onClick={() => store.forecastDailyPurchases()}
                                                className={classes.secondaryButtonForecast} />
                                        }
                                        {store.forecastDailyPurchaseStatus === ForecastDailyPurchaseStatus.Contributes &&
                                            <Typography className={classes.boldHeaderText} variant='overline'>
                                                {t('deals.label.forecastDailyPurchasesCreated')}
                                            </Typography>
                                        }
                                        <AttachFileButton id='upload-doc-button'
                                            className={classes.documentButton}
                                            title={t('deals.label.documentDialogTitle')}
                                            disableDelOrAdd={store.locked(false)}
                                            context={store.deal?.company}
                                            keyTemplate='deal-{dealId}'
                                            keyParameters={{ dealId: store.dealId ? store.dealId.replace(/-/g, '') : '' }} />
                                    </div>
                                </div>
                                <ReferenceNumberSection />
                                <SiteSection />
                                <VolumeSection />
                                <TolerancesSection />
                                <CommentSection />
                                {store.associatedVessels.length > 0
                                    ? <Button onClick={openVessel}
                                        label={t('deals.popup.goToVessel')}
                                        img={<DirectionsBoatOutlined className={classes.buttonPictogram} />}
                                        className={classes.buttonToVessel} />
                                    : null}
                                {store.deal?.mirrorDealId
                                    ? <Button
                                        label={t('deals.popup.openMirrorDeal')}
                                        onClick={() => dealDialog.open(store.deal?.mirrorDealId || '')}
                                        className={classes.secondaryButton} /> : null}
                                {canAddMovement() &&
                                    <>
                                        <Button
                                            label={t('deals.label.newMovement')}
                                            onClick={() => setShowPopupCreateMovement(true)}
                                            className={classes.secondaryButton} />
                                        {!store.locked() &&
                                            <Component.AssignMovementButton buttonClass={classes.secondaryButton} />}
                                    </>
                                }
                                {store.isBorrowLoan() &&
                                    <div className={classes.blButtonsContainer}>
                                        <div >
                                            <Button
                                                img={<Add />}
                                                label={t('stock.label.borrow')}
                                                onClick={() => newBorrowLoanMovementHandler('BORROW')}
                                                disabled={borrowLoanButtonDisabled("BORROW")}
                                                className={classes.secondaryButton} />
                                            <Button
                                                img={<Add />}
                                                label={t('stock.label.loan')}
                                                onClick={() => newBorrowLoanMovementHandler('LOAN')}
                                                disabled={borrowLoanButtonDisabled("LOAN")}
                                                className={classes.secondaryButton} />
                                        </div>
                                        <div className={classes.blValuationRow}>
                                            <div className={classes.blValuationField}>
                                                <NumberField
                                                    label={`${t('deals.label.borrowLoanValuation')} (${store.deal.quantityPriceUnit ?? ''})`}
                                                    text={store.deal?.borrowLoanValuation}
                                                    precision={3}
                                                    disabled={true}
                                                />
                                            </div>
                                        </div>
                                    </div>}
                            </Paper>
                            {store?.deal && !store.isBorrowLoan() &&
                                <Accordion TransitionProps={{ unmountOnExit: true }} expanded={store.openSections['pricingInformation']} className={classes.accordion}>
                                    <AccordionSummary expandIcon={<ExpandMore />} onClick={() => store.toggleSection('pricingInformation')}>
                                        <PricingFirstLine />
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetail}>
                                        <Paper elevation={0}>
                                            <PricingSecondLine />
                                            {!isFormulaGenericPricing && <PricingThirdLine />}
                                            {hasFeature('Pricing') && !isFormulaGenericPricing &&
                                                <Button onClick={openPricing}
                                                    label={t('deals.popup.goToPricing')}
                                                    img={<GavelOutlined className={classes.buttonPictogram} />}
                                                    className={classes.buttonToVessel} />
                                            }
                                        </Paper>
                                    </AccordionDetails>
                                </Accordion>
                            }
                            {store.deal && !store.isInternal() && !store.isBorrowLoan() && <ValidationProcess />}
                            {store?.deal && !store.isInternal() && !store.isPaperTransaction() && !store.isBorrowLoan() &&
                                <Accordion TransitionProps={{ unmountOnExit: true }} expanded={store.openSections['sap']} className={classes.accordion}>
                                    <AccordionSummary expandIcon={<ExpandMore />} onClick={() => store.toggleSection('sap')}>
                                        <Typography className={classes.paperTitle} variant='overline' display='block' gutterBottom>
                                            {t('deals.popup.sapTitle')}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetail}>
                                        <DealSapForm className={classes.dealInformation} />
                                    </AccordionDetails>
                                </Accordion>
                            }
                            {!!store?.deal?.movements?.length && !store.isInternal() && !store.isPaperTransaction() &&
                                <Accordion TransitionProps={{ unmountOnExit: true }} expanded={store.openSections['movements']}
                                    className={classes.accordion + ' ' + classes.lastSection}>
                                    <AccordionSummary expandIcon={<ExpandMore />} onClick={() => store.toggleSection('movements')}>
                                        <Typography className={classes.paperTitle} variant='overline' display='block' gutterBottom>
                                            {t('deals.label.movements')}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetail}>
                                        <Paper elevation={0}>
                                            <DealMovements />
                                        </Paper>
                                    </AccordionDetails>
                                </Accordion>
                            }
                        </>
                        : <></>}
                </div>
            </Popup>
            <DuplicateDealDialog
                isOpen={duplicateConfirmDialogOpen}
                isPeriodMode={store.isPeriodMode}
                onPeriodModeChange={store.setIsPeriodMode}
                setIsOpen={setDuplicateConfirmDialogOpen}
                startDate={store.duplicatePeriod?.start}
                endDate={store.duplicatePeriod?.end}
                isDuplicateByPeriodNotRestricted={store.isDuplicateByPeriodAndNotInternal}
                onConfirm={async () => {
                    setDuplicateConfirmDialogOpen(false)
                    await handleDuplicateDebouncer.execute()
                }}
                onDealPeriodChange={(x: DealDuplicatePeriod) => store.onDealPeriodChange(x)} />
            <CustomDialog isOpen={showPopupCreateMovement} title={t('deals.label.confirmTitle')}
                contentText={t('deals.label.confirmMovementCreation')}
                confirmButtonText={t('deals.label.createMovement')}
                onConfirm={async () => {
                    setShowPopupCreateMovement(false)
                    await handleCreateMovementDebouncer.execute()
                }}
                onClose={() => setShowPopupCreateMovement(false)}
                onCancel={() => setShowPopupCreateMovement(false)} />
            <CustomDialog isOpen={store.assignConfirmDialogOpen} title={t('deals.label.confirmAssignTitle')}
                contentText={t('deals.label.confirmAssign')}
                confirmButtonText={t('deals.label.assign')}
                onConfirm={async () => {
                    store.setAssignConfirmDialogOpen(false)
                    await handleAssignMovementsDebouncer.execute()
                    store.loadDeal(store.dealId)
                }}
                onClose={closeConfirmationPopup}
                onCancel={closeConfirmationPopup} />
            <CustomDialog isOpen={store.unAssignConfirmDialogOpen} title={t('deals.label.confirmUnassignTitle')}
                contentText={t('deals.label.confirmUnAssign')}
                confirmButtonText={t('deals.label.unAssign')}
                onConfirm={async () => {
                    store.setUnAssignConfirmDialogOpen(false)
                    if (store.dealMovement)
                        await handleUnAssignMovementsDebouncer.execute(store.dealMovement.movementId)
                    store.loadDeal(store.dealId)
                }}
                onClose={closeConfirmationPopup}
                onCancel={closeConfirmationPopup} />
            <Dialog
                open={store.dealStatusDialog.isOpen}
                onClose={() => store.setDealStatusDialog({ ...store.dealStatusDialog, isOpen: false })}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'>
                <DialogTitle>{store.dealStatusDialog.title}</DialogTitle>
                <DialogContent>
                    <Typography>{store.dealStatusDialog.description}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        label={t('components.dialogClose')}
                        className={classes.secondaryButton}
                        onClick={() => store.setDealStatusDialog({ ...store.dealStatusDialog, isOpen: false })} />
                </DialogActions>
            </Dialog>
        </>
    )
}

let styles = theme =>
    createStyles({
        dealInformation: {
            padding: '0.5em',
            marginTop: '1em',
            marginBottom: '0.5em',
        },
        buttonDiv: {
            ...defaultStyles.flexRow,
            width: '100%',
            justifyContent: 'space-between'
        },
        paperTitle: {
            color: defaultColors.red.main.color,
            marginLeft: '-0.5em'
        },
        buttonToVessel: {
            ...defaultStyles.primaryButton,
            marginTop: '2em',
            marginRight: '1em'
        },
        secondaryButton: {
            ...defaultStyles.secondaryButton,
            marginTop: '2em',
            marginLeft: '0.5em'
        },
        buttonPictogram: {
            marginRight: '0.5em'
        },
        documentButton: {
            marginRight: '0.5em'
        },
        accordionDetail: {
            flexDirection: 'column',
            marginTop: '-1em'
        },
        accordion: {
            padding: '0.5em',
            marginTop: '1em',
            marginBottom: '1em',
            borderRadius: '4px'
        },
        lastSection: {
            marginBottom: '1.5em !important'
        },
        headerRight: {
            ...defaultStyles.flexRow
        },
        boldHeaderText: {
            marginRight: '1em',
            fontWeight: 800,
            color: defaultColors.darkBlue.light.color
        },
        headerText: {
            marginRight: '0.7em'
        },
        flexRow: {
            display: 'flex',
            alignItems: 'center'
        },
        iconStyle: { height: '1.5rem', width: '1.5rem', marginTop: '-0.2rem' },
        invisible: { opacity: 0 },
        red: { color: defaultColors.red.light.color },
        grey: { color: defaultColors.grey.main.color },
        black: { color: 'black' },
        green: { color: defaultColors.green.main.color },
        secondaryButtonForecast: {
            ...defaultStyles.secondaryButton,
            marginRight: '1em'
        },
        forecastCreatedText: {
            marginRight: '1em'
        },
        dealStepper: {
            backgroundColor: 'white'
        },
        stepper: { width: '90% ' },
        cancelledDeal: { width: '10% ' },
        dealStatusInformation: {
            padding: '0.5em',
            marginTop: '1em',
            marginBottom: '0.5em',
            display: 'flex'
        },
        blButtonsContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '1em'
        },
        blValuationRow: {
            display: 'flex',
            alignItems: 'flex-end',
            gap: '0.5em',
            flexWrap: 'wrap'
        },
        blValuationField: {
            minWidth: '220px'
        }
    })

export default withStyles(styles, muiOptions)(DealEdit)
